<template>
    <TemplateDetails
      :get-function="getRecomCustomer"
      :data="customer"
      :loading="customerLoading"
    >
      <PageBack />

      <PageHeader :title="$t('customer.recom_title')" v-if="customer">
        <template v-slot:actions>
            <Button v-if="identityIsEditable" size="m" color="grey" icon="edit" @click.native.prevent="openEdit">
                {{ $t('customer.identity.edit.button') }}
            </Button>
            <Button v-if="canAnonymize" size="m" color="red" icon="trash" @click.native.prevent="openAnonymize">
                {{ $t('customer.identity.anonymize.button') }}
            </Button>
            <Button size="m" :loading="isPasswordFormLoading" @click.native.prevent="sendResetPasswordEmail"
            >{{ $t(`customer.${customer.isGuest ? 'btn_send_activation_email' : 'btn_send_reinit_email'}`) }}
            </Button>
          <ButtonLink
            v-if="customer.memberAreaUrl"
            :to="customer.memberAreaUrl"
            color="blue2"
            icon="profile"
            size="m"
            target="_blank"
            >{{ $t("customer.btn_profile") }}</ButtonLink
          >
        </template>
      </PageHeader>

      <Popin ref="popinEdit" :title="$t('customer.identity.edit.popin_title')">
        <template v-slot:content>
          <form v-if="identityForm" @submit.prevent="askEditConfirmation">
            <Field v-for="field in identityFields"
                   :key="field.name"
                   :name="field.name"
                   :type="field.type || 'text'"
                   :required="field.required"
                   :autocomplete="false"
                   :label="$t(`global.${field.name}`) + (field.required ? ' *' : '')"
                   :model="identityForm[field.name]"
                   @change="(e) => (identityForm[field.name] = e)" />
            <ButtonForm>{{ $t('global.validate') }}</ButtonForm>
          </form>
        </template>
      </Popin>

      <Popin ref="popinEditConfirm"
             :center="true"
             :title="$t('customer.identity.edit.confirm_title')"
             :text="$t('customer.identity.edit.confirm_text')">
        <template v-slot:bottom>
          <Button @click.native.prevent="cancelEditConfirmation" size="m" color="grey" icon="cross">{{ $t('global.cancel') }}</Button>
          <Button @click.native.prevent="submitEdit" size="m" color="red" icon="check" :loading="identitySaving">{{ $t('customer.identity.edit.confirm') }}</Button>
        </template>
      </Popin>

      <Popin ref="popinAnonymize"
             :center="true"
             :title="$t('customer.identity.anonymize.popin_title')"
             :text="$t('customer.identity.anonymize.popin_text')">
        <template v-slot:bottom>
          <Button @click.native.prevent="$refs.popinAnonymize.close()" size="m" color="grey" icon="cross">{{ $t('global.cancel') }}</Button>
          <Button @click.native.prevent="anonymize" size="m" color="red" icon="trash" :loading="anonymizing">{{ $t('customer.identity.anonymize.confirm') }}</Button>
        </template>
      </Popin>

      <Popin ref="popinAnonymizeBlocked"
             :center="true"
             :title="$t('customer.identity.anonymize.blocked_title')"
             :text="anonymizeBlockedText">
        <template v-slot:bottom>
          <Button @click.native.prevent="$refs.popinAnonymizeBlocked.close()" size="m" color="grey" icon="cross">{{ $t('global.close') }}</Button>
        </template>
      </Popin>

      <GridContainer>
        <GridCard>
          <GridContent>
            <SectionTableInfos :object="customer" :fields="fields" />
          </GridContent>
        </GridCard>

        <template v-slot:aside>
          <GridCard v-if="customer && customer.addresses?.length">
            <GridContent v-for="(address, i) in customer.addresses" :key="i">
              <SectionTableInfos
                :object="address"
                :fields="fieldsAddress"
                :title="$t('global.address') + ' ' + (i + 1)"
              />
            </GridContent>
          </GridCard>
          <GridCard v-if="customer && customer.nbOrders">
            <GridContent>
              <SectionTableInfos
                :object="customer"
                :fields="fieldsOrders"
                :title="$t('global.orders')"
              />
            </GridContent>
          </GridCard>

          <GridCard v-if="customer && customer.nbReprises">
            <GridContent>
              <SectionTableInfos
                :object="customer"
                :fields="fieldsResales"
                :title="$t('global.resales')"
              />
            </GridContent>
          </GridCard>
        </template>

        <template v-if="customer" v-slot:bottom>
          <div>
            <GridHeader :title="$t('menu.orders')" :count="orders.length">
              <ButtonLink v-if="orders.length"
                size="m"
                color="black"
                icon="eye"
                :to="{
                  name: 'recom_orders',
                  query: { 'customer:eq': customer.email },
                }"
                >{{ $t("customer.btn_orders") }}</ButtonLink
              >
            </GridHeader>
            <TableSticky :fields="ordersFields" :data="orders" />
          </div>

          <div>
            <GridHeader :title="$t('menu.resales')" :count="resales.length">
              <ButtonLink v-if="resales.length"
                size="m"
                color="black"
                icon="eye"
                :to="{
                  name: 'recom_resales-all',
                  query: { 'user:eq': customer.email, page: 1, limit: 25 },
                }"
                >{{ $t("customer.btn_resales") }}</ButtonLink
              >
            </GridHeader>
            <TableSticky :fields="resalesFields" :data="resales" />
          </div>

          <div>
            <GridHeader :title="$t('menu.credits')" :count="giftCards.length">
              <ButtonLink v-if="giftCards.length"
                size="m"
                color="black"
                icon="eye"
                :to="{
                  name: 'recom-vouchers',
                  query: { 'user[like]': customer.email },
                }"
                >{{ $t("customer.btn_vouchers") }}</ButtonLink
              >
            </GridHeader>
            <TableSticky :fields="giftCardsFiels" :data="giftCards" />
          </div>

          <div>
            <GridHeader :title="$t('menu.cmsCredits')" :count="credits.length">
              <ButtonLink
                v-show="roleIsAllowed(['ROLE_ADMIN_GIFTCARD', 'ROLE_BRAND_SAV'])"
                size="m"
                color="red"
                icon="plus"
                :to="{
                  name: 'recom-wallet-transactions-add',
                  params: { id: customer.id },
                }"
                >{{ $t("customer.add_credit_sav") }}</ButtonLink
              >
              <ButtonLink v-if="credits.length"
                size="m"
                color="black"
                icon="eye"
                :to="{
                  name: 'recom-vouchers',
                  query: {
                    'brand:eq': customer.brand.id,
                    'user:eq': customer.email,
                  },
                }"
                >{{ $t("customer.btn_credits") }}</ButtonLink
              >
            </GridHeader>
            <TableSticky :fields="creditsFields" :data="credits" />
          </div>
        </template>
      </GridContainer>
    </TemplateDetails>
  </template>

  <script>
  import API from "@/api/api-admin";
  import SectionTableInfos from "@/components/sections/SectionTableInfos";
  import TemplateDetails from "@/components/templates/TemplateDetails";
  import ButtonLink from "@/components/ui/button/ButtonLink";
  import GridCard from "@/components/ui/grid/GridCard";
  import GridContainer from "@/components/ui/grid/GridContainer";
  import GridContent from "@/components/ui/grid/GridContent";
  import GridHeader from "@/components/ui/grid/GridHeader";
  import PageBack from "@/components/ui/page/PageBack";
  import PageHeader from "@/components/ui/page/PageHeader";
  import TableSticky from "@/components/ui/table-sticky/TableSticky";
  import Button from "@/components/ui/button/Button";
  import ButtonForm from "@/components/ui/button/ButtonForm";
  import Field from "@/components/ui/form/fields/Field";
  import Popin from "@/components/ui/popin/Popin";
  import { mapActions, mapGetters, mapState } from "vuex";
  import { DEFAULT_REJECTION_MESSAGE } from "@/api/api-admin-interceptors";

  const ANONYMIZE_BLOCKED_REASONS = ['trade_in_in_progress', 'credit_available', 'recent_order'];
  // Messages techniques d'axios, jamais destines a l'utilisateur
  const AXIOS_ERROR_PATTERN = /^(Request failed|Network Error|timeout of)/i;

  export default {
    name: "CustomerPage",
    components: {
      GridHeader,
      TableSticky,
      GridContent,
      GridCard,
      GridContainer,
      ButtonLink,
      SectionTableInfos,
      PageHeader,
      PageBack,
      TemplateDetails,
      Button,
      ButtonForm,
      Field,
      Popin,
    },
    data() {
      return {
        fields: [
          { key: "id", type: "copy" },
          { key: "shopifyId", type: "shopifyCustomer" },
          { key: "firstName" },
          { key: "lastName" },
          { key: "email", type: "copy" },
          { key: "phone", type: "copy" },
          { key: "profile", type: "tagType" },
          {
            key: "isGuest",
            translationKey: "state",
            type: "isGuest",
            force: true,
          },
          { key: "gender", type: "gender" },
          { key: "brand", type: "client" },
          {
            key: "emailSubscription",
            translationKey: "optinEmail",
            type: "boolean",
          },
          { key: "smsSubscription", translationKey: "optinSMS", type: "boolean" },
          { key: "nbOrders", force: true },
          { key: "nbTradeInCarts", force: true, translationKey: "nbReprises" },
          { key: "createdAt", type: "date" },
          { key: "updatedAt", type: "date" },
          { key: "locale", type: "flag" },
          { key: "countryCode", size: "xs" }
        ],
        fieldsAddress: [
          { key: "firstName" },
          { key: "lastName" },
          { key: "addressLine1", translationKey: "address1" },
          { key: "addressLine2", translationKey: "address2" },
          { key: "zipCode", translationKey: "zip1" },
          { key: "city" },
          { key: "countryCode" },
        ],
        fieldsOrders: [
          { key: "nbOrders" },
          { key: "lastOrderDate", type: "date" },
        ],
        fieldsResales: [
          { key: "nbReprises" },
          { key: "lastReturnDate", type: "date" },
        ],
        orders: [],
        resales: [],
        credits: [],
        giftCards: [],
        ordersFields: [
          { key: "id", type: "eye", route: "recom_order", size: "xs" },
          { key: "reference", translationKey: "id", size: "m" },
          { key: "createdAt", translationKey: "orderDate", type: "date" },
          { key: "customer.email", type: "userEmail", size: "xl" },
          { key: "channel", translationKey: "canal" },
          { key: "total", type: "formatPrice" },
          { key: "paymentStatus", type: "tagType", size: "m" },
          {
            key: "state",
            translationKey: "orderStatus",
            type: "tagType",
            size: "m",
          },
          { key: "shippingStatus", type: "tagType", size: "m" },
          { key: "returnStatus", type: "tagType", size: "m" },
          { key: "itemsNb", translationKey: "countArticles" },
          { key: "shippingAddress.countryCode" },
        ],
        resalesFields: [
          { key: "id", type: "eye", route: "recom_resale", size: "xs" },
          { key: "brand.name", type: "client", size: "l", noSortable: true },
          { key: "reference", translationKey: "id", size: "m" },
          { key: "customer.email", type: "userEmail", size: "xl" },
          {
            key: "itemsProposedAmount",
            translationKey: "purposed_price",
            type: "formatPrice",
          },
          { key: "state", type: "tagType", size: "m" },
          {
            key: "itemsCreditedAmount",
            translationKey: "creditGranted",
            type: "formatPrice",
          },
          { key: "itemsNb", translationKey: "countArticles" },
          { key: "channel", translationKey: "canal" },
          { key: "shippingAddress.countryCode" },
        ],
        creditsFields: [
          {
            key: "createdAt",
            translationKey: "recom_transactionsDate",
            type: "date",
          },
          {
            key: "amount",
            translationKey: "recom_transactionsAmount",
            type: "formatPrice",
          },
          {
            key: "category",
            translationKey: "recom_transactionsType",
            type: "tagTypeCategory",
          },
          {
            key: "reason",
            translationKey: "recom_transactionsReason",
            type: "tagTypeReason",
            size: "xxl",
          },
          {
            key: "description",
            translationKey: "recom_transactionsDescription",
            size: "xxl",
          },
        ],
        giftCardsFiels: [
          { key: "id", type: "eye", route: "recom-voucher", size: "xs" },
          { key: "id", type: "copy", size: "xl" },
          { key: "brand", translationKey: "client", type: "client", size: "l" },
          { key: "amount", type: "formatPrice" },
          { key: "code", type: "editVoucher", size: "l" },
          { key: "createdAt", type: "date", size: "m" },
          {
            key: "expiredAt",
            translationKey: "expiresOn",
            type: "date",
            size: "m",
          },
          {
            key: "type",
            type: "voucherType",
            size: "m",
            translationKey: "channel_of_use",
          },
          {
            key: "state",
            param: "recom_voucherState",
            type: "tagType",
            size: "m",
          },
          {
            key: "burned",
            translationKey: "isBurned",
            type: "boolean",
            size: "m",
          },
          {
            key: "burnedAt",
            translationKey: "burnedAt2",
            type: "date",
            size: "m",
          },
        ],
        isPasswordFormLoading: false,
        identityFields: [
          { name: 'firstName' },
          { name: 'lastName' },
          { name: 'email', type: 'email', required: true },
          { name: 'phone', type: 'tel' },
        ],
        identityForm: null,
        identitySaving: false,
        anonymizing: false,
        anonymizeBlockedReason: null,
      };
    },
    computed: {
      ...mapState(["customer", "customerLoading"]),
      ...mapGetters(["roleIsAllowed"]),
      // Le back n'expose pas encore l'anonymisation : on la déduit de l'email qu'il réécrit
      isAnonymized() {
        const { id, email } = this.customer ?? {};
        return !!id && email?.toLowerCase() === `anonymized+${id}@faume.co`;
      },
      // canManageIdentity porte la règle de rôles mais reste vrai après anonymisation
      canAnonymize() {
        return !!this.customer?.canManageIdentity && !this.isAnonymized;
      },
      identityIsEditable() {
        return !!this.customer?.canManageIdentity && !this.isAnonymized;
      },
      anonymizeBlockedReasonValue() {
        return this.customer?.anonymizeBlockedReason ?? null;
      },
      anonymizeBlockedText() {
        if (!this.anonymizeBlockedReason) return '';
        return this.$t('customer.identity.anonymize.blocked_text', {
          reason: this.$t(`customer.identity.anonymize.blocked_reasons.${this.anonymizeBlockedReason}`)
        });
      },
    },
    methods: {
      ...mapActions(["getRecomCustomer", "exportTable", "setNotificationMessage", "updateRecomCustomer", "anonymizeRecomCustomer"]),
      openEdit() {
        this.identityForm = {
          firstName: this.customer.firstName || '',
          lastName: this.customer.lastName || '',
          email: this.customer.email || '',
          phone: this.customer.phone || '',
        };
        this.$refs.popinEdit.open();
      },
      askEditConfirmation() {
        this.$refs.popinEdit.close();
        this.$refs.popinEditConfirm.open();
      },
      cancelEditConfirmation() {
        this.$refs.popinEditConfirm.close();
        this.$refs.popinEdit.open();
      },
      submitEdit() {
        this.identitySaving = true;
        this.updateRecomCustomer({ ...this.identityForm })
          .then(() => {
            this.$refs.popinEditConfirm.close();
            this.setNotificationMessage({ text: this.$t('customer.identity.edit.success'), color: 'green' });
          })
          .catch((err) => {
            this.setNotificationMessage({ text: this.editErrorText(err), color: 'red' });
            this.cancelEditConfirmation();
          })
          .finally(() => this.identitySaving = false);
      },
      editErrorText(err) {
        if (err?.status === 403) return this.$t('customer.identity.edit.error_forbidden');

        // Le back renvoie soit une cle (errors.*), soit des violations par champ,
        // soit un message deja redige en clair
        const message = typeof err === 'string' ? err : err?.message;
        // parseApiValidationError decoupe le message sur ":" : si ce message est lui-meme
        // affichable, on ignore les fragments qu'il en a tires pour ne pas les repeter
        const messageIsShown = this.$te(message) || this.isReadableMessage(message, err);
        const violations = Object.values(err?.fieldErrors || {})
          .filter((v) => !(messageIsShown && message.includes(v)));

        const parts = [];
        [message, ...violations].filter(Boolean).forEach((msg) => {
          const text = this.$te(msg) ? this.$t(msg) : (this.isReadableMessage(msg, err) ? msg : null);
          if (text && !parts.includes(text)) parts.push(text);
        });

        if (parts.length) return parts.join('\n');
        return this.$t('customer.identity.edit.error');
      },
      // Un message brut n'est affiche que si le back l'a redige pour l'utilisateur
      isReadableMessage(msg, err) {
        if (typeof msg !== 'string' || !msg.trim()) return false;
        // Hors validation, le message vient d'axios ou de Symfony : jamais affichable
        if (err?.status !== 422) return false;
        const trimmed = msg.trim();
        if (trimmed === DEFAULT_REJECTION_MESSAGE || AXIOS_ERROR_PATTERN.test(trimmed)) return false;
        // Ecarte les cles techniques type "errors.x" ou "email: errors.x"
        return !/(^|\s)[a-z0-9_]+(\.[a-z0-9_]+)+$/i.test(trimmed) && /\s/.test(trimmed);
      },
      openAnonymize() {
        if (this.anonymizeBlockedReasonValue) {
          this.showAnonymizeBlocked(this.anonymizeBlockedReasonValue);
          return;
        }
        this.$refs.popinAnonymize.open();
      },
      showAnonymizeBlocked(reason) {
        this.anonymizeBlockedReason = reason;
        this.$refs.popinAnonymizeBlocked.open();
      },
      anonymize() {
        this.anonymizing = true;
        this.anonymizeRecomCustomer()
          .then(() => {
            this.$refs.popinAnonymize.close();
            this.setNotificationMessage({ text: this.$t('customer.identity.anonymize.success'), color: 'green' });
          })
          .catch((err) => {
            this.$refs.popinAnonymize.close();
            const reason = typeof err === 'string' ? err : err?.message;
            if (ANONYMIZE_BLOCKED_REASONS.includes(reason)) {
              this.showAnonymizeBlocked(reason);
            } else {
              this.setNotificationMessage({ text: this.$t('customer.identity.anonymize.error'), color: 'red' });
            }
          })
          .finally(() => this.anonymizing = false);
      },
      getOrders() {
        API.get(
          `${this.$listRoutes.recomCustomerOrders}`.replace(
            ":id",
            this.customer.id
          )
        ).then((res) => (this.orders = res.data ?? []));
      },
      getResales() {
        API.get(
          `${this.$listRoutes.recomCustomerTradeins}`.replace(
            ":id",
            this.customer.id
          )
        ).then((res) => (this.resales = res.data ?? []));
      },
      getCredits() {
        API.get(
          `${this.$listRoutes.recomCustomerTransactions}`.replace(
            ":id",
            this.customer.id
          )
        ).then((res) => (this.credits = res.data ?? []));
      },
      getGiftCards() {
        API.get(
          `${this.$listRoutes.recomVouchers.replace(
            ":id",
            this.customer.id
          )}?page=1&limit=100&user[like]=${window.encodeURIComponent(
            this.customer.email
          )}`
        ).then((res) => (this.giftCards = res.data?.items ?? []));
      },
      sendResetPasswordEmail() {
          this.isPasswordFormLoading = true;
          API.post(`${this.$listRoutes.recomCustomerResetPassword.replace(':id', this.customer.id)}`, {})
              .then(() => {
                  const message = this.$t(`customer.${this.customer.isGuest ? 'activation_password_success' : 'reset_password_success'}`);
                  this.setNotificationMessage(message);
              })
              .catch(() => {
                  this.setNotificationMessage({text: this.$t("customer.reset_password_error"), color: "red"});
              })
              .finally(() => this.isPasswordFormLoading = false)
    },
    },
    watch: {
      customer() {
        if (this.customer) {
          this.getOrders();
          this.getResales();
          this.getCredits();
          this.getGiftCards();
        }
      },
    },
    mounted() {
      if (this.customer) {
        this.getOrders();
        this.getResales();
        this.getCredits();
        this.getGiftCards();
      }
    },
  };
  </script>
  